import React from 'react';
import { Link } from 'react-router-dom';
import SEO, { SEOPages } from '../components/SEO';
import { 
  Shield, 
  TrendingUp, 
  AlertCircle, 
  CheckCircle, 
  ArrowRight,
  PiggyBank,
  Calculator,
  FileText,
  Award
} from 'lucide-react';
import LeadMagnetBox from '../components/LeadMagnetBox';
import MiniSimulator3emePilier from '../components/MiniSimulator3emePilier';

const Guide3emePilier = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0B0F12] via-[#0C1D3D] to-[#0B0F12]">
      <SEO {...SEOPages.guide3emePilier} />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-purple-500/10" />
        <div className="max-w-4xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent leading-tight">
              Guide Complet 3ème Pilier Suisse
            </h1>
            <p className="text-xl text-gray-300 mb-4">
              Tout ce que vous devez savoir pour optimiser votre prévoyance et réduire vos impôts
            </p>
            <p className="text-sm text-gray-400">
              Par ATOM Group SA • Mis à jour le {new Date().toLocaleDateString('fr-CH', { day: 'numeric', month: 'long', year: 'numeric' })} • Lecture : 12 minutes
            </p>
          </div>

          {/* Lead Magnet Top */}
          <LeadMagnetBox position="top" />
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          
          {/* Applicabilité nationale */}
          <div className="p-5 bg-blue-500/10 border-l-4 border-blue-500 rounded-r-lg mb-8">
            <p className="text-gray-200 text-sm">
              <strong>🇨🇭 Guide applicable dans toute la Suisse</strong> • Les règles du 3ème pilier sont fédérales (plafonds, conditions de retrait). Nos exemples fiscaux s'appliquent à tous les cantons romands (Vaud, Genève, Neuchâtel, Fribourg, Valais, Jura), avec des variations selon les barèmes cantonaux. <strong>Mis à jour : {new Date().toLocaleDateString('fr-CH', { month: 'long', year: 'numeric' })}</strong>
            </p>
          </div>
          
          {/* Introduction */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <h2 className="text-3xl font-bold text-white mb-6">Qu'est-ce que le 3ème pilier ?</h2>
            <p className="text-gray-300 mb-4 leading-relaxed">
              Le 3ème pilier est un élément fondamental du système de prévoyance suisse, complétant l'AVS (1er pilier) et la prévoyance professionnelle (2ème pilier). Il permet de constituer un capital pour la retraite tout en bénéficiant d'avantages fiscaux significatifs.
            </p>
            <p className="text-gray-300 mb-4 leading-relaxed">
              En Suisse romande, le 3ème pilier est un outil d'optimisation fiscale incontournable qui peut vous faire économiser des milliers de francs chaque année.
            </p>
            
            {/* Atom Independence */}
            <div className="mt-6 p-4 bg-blue-500/10 border-l-4 border-blue-500 rounded-r-lg">
              <p className="text-gray-200 text-sm italic">
                <strong>ATOM Group est un cabinet 100% indépendant,</strong> non lié à une banque ou assurance. Nos recommandations sont objectives et basées uniquement sur votre intérêt.
              </p>
            </div>
          </div>

          {/* Encart PDF Premium après intro */}

          {/* CTA après introduction */}
          

          {/* Plafonds 2026 */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <div className="flex items-center gap-3 mb-6">
              <PiggyBank className="w-8 h-8 text-blue-400" />
              <h2 className="text-3xl font-bold text-white">Plafonds 2026</h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="p-6 bg-gradient-to-br from-blue-500/20 to-blue-600/20 rounded-xl border border-blue-500/30">
                <h3 className="text-xl font-bold text-white mb-2">3a (Pilier lié)</h3>
                <p className="text-4xl font-bold text-blue-400 mb-3">CHF 7'258</p>
                <p className="text-gray-300 text-sm">Pour les salariés affiliés au 2ème pilier</p>
              </div>
              <div className="p-6 bg-gradient-to-br from-purple-500/20 to-purple-600/20 rounded-xl border border-purple-500/30">
                <h3 className="text-xl font-bold text-white mb-2">3a (Indépendants)</h3>
                <p className="text-4xl font-bold text-purple-400 mb-3">CHF 36'288</p>
                <p className="text-gray-300 text-sm">Pour les indépendants sans 2ème pilier (20% du revenu net, max.)</p>
              </div>
            </div>

            <div className="mt-6 p-4 bg-yellow-500/10 border-l-4 border-yellow-500 rounded-r-lg">
              <p className="text-gray-200 text-sm">
                <strong>Important :</strong> Ces montants sont déductibles fiscalement. Ne pas les utiliser, c'est payer plus d'impôts inutilement.
              </p>
            </div>

            <p className="text-xs text-gray-400 mt-4 text-center italic">
              Sources : OFAS • Barèmes fiscaux cantonaux 2025 • Estimations ATOM Group SA
            </p>
          </div>

          {/* CTA après tableau plafonds */}
          

          {/* 3a vs 3b */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <h2 className="text-3xl font-bold text-white mb-6">3a vs 3b : Quelle différence ?</h2>
            
            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead>
                  <tr className="border-b border-white/20">
                    <th className="text-left py-3 px-2 md:py-4 md:px-4 text-gray-300 font-semibold text-sm md:text-base">Critère</th>
                    <th className="text-left py-3 px-2 md:py-4 md:px-4 text-blue-400 font-semibold text-sm md:text-base">3a (Pilier lié)</th>
                    <th className="text-left py-3 px-2 md:py-4 md:px-4 text-purple-400 font-semibold text-sm md:text-base">3b (Pilier libre)</th>
                  </tr>
                </thead>
                <tbody className="text-gray-300 text-sm md:text-base">
                  <tr className="border-b border-white/10">
                    <td className="py-3 px-2 md:py-4 md:px-4 font-medium">Déduction fiscale</td>
                    <td className="py-3 px-2 md:py-4 md:px-4">✅ Oui (max CHF 7'258)</td>
                    <td className="py-3 px-2 md:py-4 md:px-4">⚠️ Limitée</td>
                  </tr>
                  <tr className="border-b border-white/10">
                    <td className="py-3 px-2 md:py-4 md:px-4 font-medium">Flexibilité retrait</td>
                    <td className="py-3 px-2 md:py-4 md:px-4">❌ Conditions strictes</td>
                    <td className="py-3 px-2 md:py-4 md:px-4">✅ Libre</td>
                  </tr>
                  <tr className="border-b border-white/10">
                    <td className="py-3 px-2 md:py-4 md:px-4 font-medium">Montant max</td>
                    <td className="py-3 px-2 md:py-4 md:px-4">CHF 7'258</td>
                    <td className="py-3 px-2 md:py-4 md:px-4">Illimité</td>
                  </tr>
                  <tr className="border-b border-white/10">
                    <td className="py-3 px-2 md:py-4 md:px-4 font-medium">Âge de retrait</td>
                    <td className="py-3 px-2 md:py-4 md:px-4">5 ans avant AVS</td>
                    <td className="py-3 px-2 md:py-4 md:px-4">Flexible</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-2 md:py-4 md:px-4 font-medium">Idéal pour</td>
                    <td className="py-3 px-2 md:py-4 md:px-4">Optimisation fiscale</td>
                    <td className="py-3 px-2 md:py-4 md:px-4">Épargne flexible</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="mt-6 p-4 bg-green-500/10 border-l-4 border-green-500 rounded-r-lg">
              <p className="text-gray-200 text-sm">
                <strong>Notre recommandation ATOM :</strong> Privilégiez le 3a pour maximiser vos déductions fiscales, puis complétez avec un 3b si vous souhaitez plus de flexibilité.
              </p>
            </div>

            <p className="text-xs text-gray-400 mt-4 text-center italic">
              Sources : OFAS • Barèmes fiscaux cantonaux 2025 • Estimations ATOM Group SA
            </p>
          </div>

          {/* CTA après tableau 3a vs 3b */}
          

          {/* Simulateur */}
          <MiniSimulator3emePilier />

          {/* Avantages fiscaux */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <div className="flex items-center gap-3 mb-6">
              <TrendingUp className="w-8 h-8 text-green-400" />
              <h2 className="text-3xl font-bold text-white">Avantages fiscaux concrets</h2>
            </div>

            <p className="text-gray-300 mb-6 leading-relaxed">
              Le 3ème pilier vous permet de déduire vos cotisations de votre revenu imposable. Voici des exemples concrets en Suisse romande :
            </p>

            <div className="space-y-4">
              <div className="p-6 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-xl border border-green-500/30">
                <h4 className="font-bold text-white mb-2">💼 Célibataire - Revenu CHF 80'000</h4>
                <p className="text-gray-300 mb-2">Versement 3a : CHF 7'258</p>
                <p className="text-2xl font-bold text-green-400">Économie fiscale : ~CHF 1'500 / an</p>
                <p className="text-sm text-gray-400 mt-1">Taux marginal estimé : 20-22%</p>
              </div>

              <div className="p-6 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-xl border border-green-500/30">
                <h4 className="font-bold text-white mb-2">👨‍👩‍👧 Couple marié - Revenu CHF 150'000</h4>
                <p className="text-gray-300 mb-2">2 × Versement 3a : CHF 14'516</p>
                <p className="text-2xl font-bold text-green-400">Économie fiscale : ~CHF 3'600 / an</p>
                <p className="text-sm text-gray-400 mt-1">Taux marginal estimé : 25-28%</p>
              </div>

              <div className="p-6 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-xl border border-green-500/30">
                <h4 className="font-bold text-white mb-2">🚀 Cadre - Revenu CHF 200'000</h4>
                <p className="text-gray-300 mb-2">Versement 3a maximum : CHF 7'258</p>
                <p className="text-2xl font-bold text-green-400">Économie fiscale : ~CHF 2'200 / an</p>
                <p className="text-sm text-gray-400 mt-1">Taux marginal estimé : 30-32%</p>
              </div>
            </div>

            <div className="mt-6 p-4 bg-blue-500/10 border-l-4 border-blue-500 rounded-r-lg">
              <p className="text-gray-200 text-sm italic">
                <strong>ATOM Group est un cabinet 100% indépendant.</strong> Nos recommandations sont objectives et basées uniquement sur votre intérêt.
              </p>
            </div>
          </div>

          {/* CTA après avantages fiscaux */}
          

          {/* CTA WhatsApp rapide */}
          

          {/* Lead Magnet Middle */}

          {/* Cas pratiques */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <div className="flex items-center gap-3 mb-6">
              <FileText className="w-8 h-8 text-purple-400" />
              <h2 className="text-3xl font-bold text-white">Cas pratiques</h2>
            </div>

            <div className="space-y-6">
              <div className="p-6 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-xl border border-blue-500/30">
                <h4 className="text-xl font-bold text-white mb-3">📊 Cas 1 : Mathieu, 28 ans, informaticien</h4>
                <p className="text-gray-300 mb-2"><strong>Situation :</strong> Salaire CHF 85'000, célibataire, locataire</p>
                <p className="text-gray-300 mb-4"><strong>Objectif :</strong> Maximiser épargne retraite et réduire impôts</p>
                <p className="text-green-400 font-semibold mb-2">✅ Solution ATOM :</p>
                <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4">
                  <li>Ouvrir 2 comptes 3a avec CHF 7'258 total</li>
                  <li>Économie fiscale immédiate : ~CHF 1'600/an</li>
                  <li>Sur 35 ans : Capital de ~CHF 380'000 (avec rendement 2%)</li>
                  <li>Économies fiscales cumulées : ~CHF 56'000</li>
                </ul>
              </div>

              <div className="p-6 bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-xl border border-purple-500/30">
                <h4 className="text-xl font-bold text-white mb-3">👨‍👩‍👧 Cas 2 : Sophie & Marc, couple avec 2 enfants</h4>
                <p className="text-gray-300 mb-2"><strong>Situation :</strong> Revenus combinés CHF 180'000, propriétaires avec hypothèque</p>
                <p className="text-gray-300 mb-4"><strong>Objectif :</strong> Optimiser fiscalité et préparer études des enfants</p>
                <p className="text-green-400 font-semibold mb-2">✅ Solution ATOM :</p>
                <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4">
                  <li>2 × CHF 7'258 en 3a (chacun son compte)</li>
                  <li>Économie fiscale combinée : ~CHF 4'200/an</li>
                  <li>3b libre pour projets à moyen terme (études, travaux)</li>
                  <li>Retraits échelonnés optimisés fiscalement</li>
                </ul>
              </div>

              <div className="p-6 bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-xl border border-green-500/30">
                <h4 className="text-xl font-bold text-white mb-3">🚀 Cas 3 : Lucie, indépendante</h4>
                <p className="text-gray-300 mb-2"><strong>Situation :</strong> Revenu net CHF 120'000, sans 2ème pilier</p>
                <p className="text-gray-300 mb-4"><strong>Objectif :</strong> Constituer prévoyance complète</p>
                <p className="text-green-400 font-semibold mb-2">✅ Solution ATOM :</p>
                <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4">
                  <li>Plafond spécial indépendant : jusqu'à CHF 36'288</li>
                  <li>Versement optimal : CHF 24'000 (20% du revenu)</li>
                  <li>Économie fiscale : ~CHF 7'200/an</li>
                  <li>Compenser l'absence de 2ème pilier</li>
                </ul>
              </div>
            </div>
          </div>

          {/* CTA Simulation instantanée */}
          <div className="glass p-8 rounded-2xl border border-purple-500/30 mb-8 bg-gradient-to-br from-purple-500/10 to-pink-500/10">
            <div className="text-center">
              <div className="inline-block bg-gradient-to-br from-purple-500 to-pink-600 p-4 rounded-xl mb-4">
                <Calculator className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">
                💡 Vous souhaitez connaître votre économie fiscale exacte ?
              </h3>
              <p className="text-gray-300 mb-6">
                Obtenez une simulation précise en 45 secondes
              </p>
              <a
                href="#simulateur"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('#simulateur')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-500 to-pink-600 text-white px-8 py-4 rounded-xl font-semibold hover:from-purple-600 hover:to-pink-700 transition-all transform hover:scale-105 text-lg"
              >
                Lancer la simulation ATOM (gratuit – 45 secondes)
                <ArrowRight className="w-6 h-6" />
              </a>
            </div>
          </div>

          {/* Optimisation cantonale */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <div className="flex items-center gap-3 mb-6">
              <Award className="w-8 h-8 text-yellow-400" />
              <h2 className="text-3xl font-bold text-white">Stratégies d'optimisation avancées</h2>
            </div>

            <p className="text-gray-300 mb-6 leading-relaxed">
              Voici des stratégies d'optimisation fiscale applicables dans tous les cantons romands :
            </p>

            <div className="space-y-4">
              <div className="p-6 bg-blue-500/10 border-l-4 border-blue-500 rounded-r-lg">
                <h4 className="font-bold text-white mb-2">📊 Comprendre le barème de votre canton</h4>
                <p className="text-gray-300 text-sm">
                  Chaque canton applique son propre barème fiscal. La déduction 3a est particulièrement efficace dans les cantons avec barème progressif élevé (Genève, Vaud, Neuchâtel).
                </p>
              </div>

              <div className="p-6 bg-blue-500/10 border-l-4 border-blue-500 rounded-r-lg">
                <h4 className="font-bold text-white mb-2">💰 Taxation au retrait variable</h4>
                <p className="text-gray-300 text-sm">
                  Le taux d'imposition sur les retraits de 3ème pilier varie selon les cantons (généralement entre 3% et 12%), le montant retiré et votre situation familiale.
                </p>
              </div>

              <div className="p-6 bg-blue-500/10 border-l-4 border-blue-500 rounded-r-lg">
                <h4 className="font-bold text-white mb-2">🏡 Stratégie immobilière</h4>
                <p className="text-gray-300 text-sm">
                  Utilisez votre 3a pour financer l'achat de votre résidence principale : double avantage fiscal (déduction + retrait anticipé sans pénalité).
                </p>
              </div>

              <div className="p-6 bg-blue-500/10 border-l-4 border-blue-500 rounded-r-lg">
                <h4 className="font-bold text-white mb-2">📅 Retraits échelonnés optimaux</h4>
                <p className="text-gray-300 text-sm">
                  Stratégie avancée : ouvrez 3-4 comptes 3a et retirez-les sur différentes années fiscales pour minimiser la taxation progressive.
                </p>
              </div>
            </div>

            <div className="mt-6 p-4 bg-blue-500/10 border-l-4 border-blue-500 rounded-r-lg">
              <p className="text-gray-200 text-sm italic">
                <strong>ATOM Group est un cabinet 100% indépendant.</strong> Nos recommandations sont objectives et basées uniquement sur votre intérêt.
              </p>
            </div>
          </div>

          {/* FAQ */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <h2 className="text-3xl font-bold text-white mb-6">Questions fréquentes</h2>
            
            <div className="space-y-4">
              {[
                {
                  q: "Puis-je retirer mon 3a avant la retraite ?",
                  a: "Oui, dans certains cas : achat résidence principale, départ définitif de Suisse, début activité indépendante, invalidité, ou rachat 2ème pilier."
                },
                {
                  q: "Combien de comptes 3a puis-je avoir ?",
                  a: "Il n'y a pas de limite légale. Nous recommandons 2-4 comptes pour optimiser les retraits échelonnés et réduire la fiscalité au retrait."
                },
                {
                  q: "3a bancaire ou 3a assurance : que choisir ?",
                  a: "Le 3a bancaire offre plus de flexibilité et généralement de meilleurs rendements. Le 3a assurance peut inclure une couverture décès/invalidité."
                },
                {
                  q: "Quand verser mon 3a pour optimiser ?",
                  a: "Idéalement en début d'année (janvier-février) pour maximiser les intérêts composés. Au plus tard le 31 décembre pour la déduction fiscale de l'année."
                },
                {
                  q: "Que se passe-t-il en cas de décès ?",
                  a: "Le capital est versé aux bénéficiaires selon l'ordre légal (conjoint/partenaire enregistré, enfants, puis autres bénéficiaires selon l'OPP3)."
                }
              ].map((faq, index) => (
                <div key={index} className="p-6 bg-white/5 rounded-xl border border-white/10 hover:border-blue-500/30 transition-all">
                  <h4 className="font-bold text-white mb-2 flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    {faq.q}
                  </h4>
                  <p className="text-gray-300 text-sm ml-7">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Lead Magnet Bottom */}

          {/* Conclusion */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <h2 className="text-3xl font-bold text-white mb-6">Conclusion</h2>
            <p className="text-gray-300 mb-4 leading-relaxed">
              Le 3ème pilier est un outil puissant d'optimisation fiscale et de prévoyance en Suisse. Dans tous les cantons romands, les conditions sont avantageuses pour ceux qui savent en tirer parti.
            </p>
            <p className="text-gray-300 mb-6 leading-relaxed">
              <strong className="text-white">Ne laissez pas passer cette opportunité :</strong> chaque année sans 3ème pilier, ce sont des milliers de francs d'économies fiscales perdues définitivement.
            </p>

            <div className="p-6 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-xl border border-blue-500/30">
              <p className="text-white font-semibold mb-4">
                🎯 ATOM Group vous accompagne pour :
              </p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  Analyser votre situation fiscale actuelle
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  Choisir les meilleurs produits 3a adaptés à votre profil
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  Optimiser votre stratégie de retraits
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  Maximiser vos économies fiscales chaque année
                </li>
              </ul>
            </div>

            <div className="mt-6 text-center">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-8 py-4 rounded-xl font-semibold hover:from-blue-600 hover:to-purple-700 transition-all transform hover:scale-105 text-lg"
              >
                Réserver mon analyse gratuite maintenant
                <ArrowRight className="w-6 h-6" />
              </Link>
              <p className="text-gray-400 text-sm mt-3">
                🕐 Rendez-vous de 30 minutes • 100% gratuit • Sans engagement
              </p>
            </div>
          </div>

        </div>
      </section>

      {/* Sticky Banner */}

      {/* Exit Intent Popup */}
    </div>
  );
};

export default Guide3emePilier;
